/**
 * useBilling — hooks for Stripe billing endpoints.
 *
 * GET  /api/billing/status                  → useBillingStatus()
 * POST /api/billing/portal                  → useCreateBillingPortal()
 * POST /api/billing/promo                   → useApplyPromoCode()
 * GET  /jsonapi/commerce_license/role       → useLicenses()
 * GET  /jsonapi/commerce_payment/payment_default → usePaymentHistory(originatingOrderId)
 *
 * useLicenses() works for all authenticated users (entity access scopes automatically).
 * useSubscriptions() is kept for backward compat but only visible to subscription owners.
 */
import { useQuery, useMutation } from '@tanstack/react-query';
import apiClient from '@/lib/axios';
import { useUIStore } from '@/stores/uiStore';

/**
 * Get the Stripe subscription status for the current Site Owner.
 * Returns a BillingStatus object with status, plan, current_period_end, etc.
 */
export function useBillingStatus() {
  return useQuery({
    queryKey: ['billingStatus'],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get('/api/billing/status', { signal });
      return data; // BillingStatus
    },
    staleTime: 5 * 60_000,
    retry: false, // Don't retry on 403 (non-owners visiting the page)
  });
}

/**
 * Create a Stripe Customer Portal session.
 * Returns { portal_url } — the caller should redirect to this URL immediately.
 *
 * @param {string} [returnUrl]  Where Stripe sends the user after they leave the portal.
 */
export function useCreateBillingPortal() {
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: async (returnUrl) => {
      const body = returnUrl ? { return_url: returnUrl } : {};
      const { data } = await apiClient.post('/api/billing/portal', body);
      return data; // { portal_url }
    },
    onError: (err) => {
      addToast({
        title: 'Billing portal unavailable',
        description: err?.response?.data?.error || 'Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Get commerce licenses for the current authenticated user.
 *
 * Drupal entity access scopes the response to the logged-in user automatically —
 * no uid filter is required or useful here.
 * Includes product_variation to derive tier, period and price.
 * Deduplicates by originating_order so multiple role grants from one subscription
 * appear as a single billing entry.
 *
 * Returns an array of:
 *   { id, state, tier, period, price, granted, expires, originatingOrderId }
 */
export function useLicenses() {
  return useQuery({
    queryKey: ['licenses'],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get('/jsonapi/commerce_license/role', {
        params: { include: 'product_variation', sort: '-created' },
        signal,
      });

      const included = data?.included ?? [];
      const seen = new Set();

      return (data?.data ?? []).reduce((acc, l) => {
        const originatingOrderId = l.relationships?.originating_order?.data?.id ?? null;

        // One billing entry per originating order (multiple roles granted per order)
        if (originatingOrderId && seen.has(originatingOrderId)) return acc;
        if (originatingOrderId) seen.add(originatingOrderId);

        const variationId = l.relationships?.product_variation?.data?.id ?? null;
        const variation   = variationId ? included.find((i) => i.id === variationId) ?? null : null;

        const scheduleId =
          variation?.relationships?.billing_schedule?.data?.meta?.drupal_internal__target_id ?? '';
        const period = scheduleId.includes('annual') ? 'annual' : 'monthly';

        const roleId =
          l.relationships?.license_role?.data?.meta?.drupal_internal__target_id ?? '';
        let tier = null;
        if (roleId.includes('starter'))       tier = 'Starter';
        else if (roleId.includes('growth'))   tier = 'Growth';
        else if (roleId.includes('network'))  tier = 'Network';
        else if (roleId.includes('scale'))    tier = 'Scale';

        // Commerce uses epoch (1970) as the sentinel for "unlimited" expiry
        const expiresRaw  = l.attributes?.expires ?? null;
        const isUnlimited = !expiresRaw || new Date(expiresRaw).getFullYear() < 2000;

        acc.push({
          id: l.id,
          state:              l.attributes?.state ?? 'unknown',
          tier,
          period,
          price:              variation?.attributes?.price ?? null, // { number, currency_code, formatted }
          granted:            l.attributes?.granted ?? null,
          expires:            isUnlimited ? null : expiresRaw,
          originatingOrderId,
        });

        return acc;
      }, []);
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * @deprecated Prefer useLicenses() — commerce_subscription is only accessible
 * to the subscription owner; contributors must use commerce_license/role instead.
 */
export function useSubscriptions(userUuid) {
  return useQuery({
    queryKey: ['subscriptions', userUuid],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get('/jsonapi/commerce_subscription/license', {
        params: {
          'filter[uid.drupal_internal__uid]': userUuid,
          sort: '-created',
        },
        signal,
      });
      return (data?.data ?? []).map((s) => ({
        id: s.id,
        title: s.attributes?.title ?? null,
        state: s.attributes?.state ?? 'unknown',
        unitPrice: s.attributes?.unit_price ?? null,   // { number, currency_code, formatted }
        nextRenewal: s.attributes?.next_renewal ?? null,
        starts: s.attributes?.starts ?? null,
        ends: s.attributes?.ends ?? null,
        trialEnds: s.attributes?.trial_ends ?? null,
        initialOrderId: s.relationships?.initial_order?.data?.id ?? null,
      }));
    },
    enabled: !!userUuid,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function usePaymentHistory(initialOrderId) {
  return useQuery({
    queryKey: ['paymentHistory', initialOrderId],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get('/jsonapi/commerce_payment/payment_default', {
        params: {
          'filter[order_id.id]': initialOrderId,
          sort: '-completed',
          'page[limit]': 50,
        },
        signal,
      });
      return (data?.data ?? []).map((p) => ({
        id: p.id,
        drupalPaymentId: p.attributes?.drupal_internal__payment_id ?? null,
        drupalOrderId:   p.relationships?.order_id?.data?.meta?.drupal_internal__target_id ?? null,
        amount: p.attributes?.amount ?? null,           // { number, currency_code, formatted }
        refundedAmount: p.attributes?.refunded_amount ?? null,
        state: p.attributes?.state ?? 'unknown',
        completed: p.attributes?.completed ?? null,
      }));
    },
    enabled: !!initialOrderId,
    staleTime: 2 * 60_000,
    retry: false,
  });
}

export function useApplyPromoCode() {
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: async (promoCode) => {
      const { data } = await apiClient.post('/api/billing/promo', {
        promo_code: promoCode,
      });
      return data;
    },
    onSuccess: (result) => {
      addToast({
        title: 'Promo code applied',
        description: result.message || `${result.discount_percent ?? ''}% discount applied.`,
        variant: 'success',
      });
    },
    onError: (err) => {
      addToast({
        title: 'Promo code failed',
        description: err?.response?.data?.error || 'Invalid or expired code.',
        variant: 'destructive',
      });
    },
  });
}
